<script>
    import { searchProps } from "../../../../vulnerability/vulnerabilities.state.svelte";
    import vulnerabilitiesApi from "../../../../vulnerability/vulnerabilities.api.js";
    import tagService from "../../../../tag/service.js";
    import Spinner from "../../../../../components/spinner.svelte"
    import BreadCrumbNav from "../../../../../components/breadcrum-nav.svelte";
    import powerTableService from "../../../../../services/powerTable.js";
    import PropValueRow from "../../../../../components/powerTable/prop-value-row.svelte";
    import CountByPropValue from "../../../_components/count-by-prop-value.svelte";
    import VulnerabilityList from "../../../../vulnerability/_components/v2-list.svelte";
    import VulnerabilityTitleRow from "../../../../../components/powerTable/vulnerability-title-row.svelte";
    import { PowerTable } from "@muonw/powertable";
    import { format, locale } from "svelte-i18n";
    import { token } from "../../../../user/user.state.svelte";
    import { assets_kinds } from "../../../../asset/assets.api.js";
    import { params, goto } from "@roxi/routify";
    import dayjs from "dayjs";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";
    import flatpickr from "flatpickr";
    import "flatpickr/dist/flatpickr.css";
    import { Spanish, English } from "flatpickr/dist/l10n/es.js";

    // Safely bind router navigation synchronously at root level during component setup
    const redirect = $goto;

    let kind = $derived($params.kind ? decodeURIComponent($params.kind) : "");
    let name = $derived($params.name ? decodeURIComponent($params.name) : "");
    
    let tableElement = $state();

    // Active filters driven strictly by the URL
    let currentFilter = $derived.by(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));
        const active = {};
        if (paramsCopy.asset__kind__in) active.asset__kind__in = paramsCopy.asset__kind__in;
        if (paramsCopy.finding__status__in) active.finding__status__in = paramsCopy.finding__status__in;
        if (paramsCopy.vulnerability__level__in) active.vulnerability__level__in = paramsCopy.vulnerability__level__in;
        if (paramsCopy.vulnerability_tag_id) active.vulnerability_tag_id = paramsCopy.vulnerability_tag_id;
        if (paramsCopy.vulnerability__id__ilike) active.vulnerability__id__ilike = paramsCopy.vulnerability__id__ilike;
        if (paramsCopy.finding__first_discovery_at__gte) active.finding__first_discovery_at__gte = paramsCopy.finding__first_discovery_at__gte;
        if (paramsCopy.finding__first_discovery_at__lte) active.finding__first_discovery_at__lte = paramsCopy.finding__first_discovery_at__lte;
        if (paramsCopy.finding__last_discovery_at__gte) active.finding__last_discovery_at__gte = paramsCopy.finding__last_discovery_at__gte;
        if (paramsCopy.finding__last_discovery_at__lte) active.finding__last_discovery_at__lte = paramsCopy.finding__last_discovery_at__lte;
        return active;
    });

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        finding__status__in: [],
        vulnerability__level__in: [],
        vulnerability__id__ilike: "",
        vulnerability_tag_id: "",
        finding__first_discovery_at: "",
        finding__last_discovery_at: "",
    });

    const dateInputs = [
        {
            id: "finding__first_discovery_at",
            name: "finding__first_discovery_at",
            label: $format("components.advancedSearch.findings_created") || "First discovery at",
        },
        {
            id: "finding__last_discovery_at",
            name: "finding__last_discovery_at",
            label: $format("components.advancedSearch.findings_seen") || "Last discovery at",
        },
    ];

    let priorityOptions = [
        { name: $format("vulnerability.severity.critical").charAt(0).toUpperCase() + $format("vulnerability.severity.critical").slice(1), id: "critical" },
        { name: $format("vulnerability.severity.high").charAt(0).toUpperCase() + $format("vulnerability.severity.high").slice(1), id: "high" },
        { name: $format("vulnerability.severity.medium").charAt(0).toUpperCase() + $format("vulnerability.severity.medium").slice(1), id: "medium" },
        { name: $format("vulnerability.severity.low").charAt(0).toUpperCase() + $format("vulnerability.severity.low").slice(1), id: "low" },
        { name: $format("vulnerability.severity.info").charAt(0).toUpperCase() + $format("vulnerability.severity.info").slice(1), id: "info" },
    ];

    let vulKindOptions = [
        { name: $format("vulnerability.kind.ad"), id: "AD-" },
        { name: $format("vulnerability.kind.asvs"), id: "ASVS-" },
        { name: $format("vulnerability.kind.cve"), id: "CVE-" },
        { name: $format("vulnerability.kind.info"), id: "INFO-" },
        { name: $format("vulnerability.kind.leak"), id: "LEAK-" },
        { name: $format("vulnerability.kind.nvt"), id: "NVT-" },
        { name: $format("vulnerability.kind.sve"), id: "SVE-" },
        { name: $format("vulnerability.kind.eol"), id: "EOL-" },
        { name: $format("vulnerability.kind.dmarc"), id: "DMARC-" },
        { name: $format("vulnerability.kind.spf"), id: "SPF-" },
        { name: $format("vulnerability.kind.m365"), id: "M365-" },
        { name: $format("vulnerability.kind.code"), id: "CODE-" },
        { name: $format("vulnerability.kind.custom"), id: "X-" },
    ];

    let statusOptions = [
        { name: $format("finding.status.open"), id: "open" },
        { name: $format("finding.status.accepted"), id: "accepted" },
        { name: $format("finding.status.fixed"), id: "fixed" },
        { name: $format("finding.status.false_positive"), id: "false_positive" },
    ];

    let translatedAssetKinds = $derived(
        assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const p = currentFilter;

        const toArray = (val) => val ? (typeof val === "string" ? val.split(",") : val) : [];

        let first_gte = p.finding__first_discovery_at__gte ? dayjs(p.finding__first_discovery_at__gte).format("YYYY/MM/DD") : "";
        let first_lte = p.finding__first_discovery_at__lte ? dayjs(p.finding__first_discovery_at__lte).format("YYYY/MM/DD") : "";
        let last_gte = p.finding__last_discovery_at__gte ? dayjs(p.finding__last_discovery_at__gte).format("YYYY/MM/DD") : "";
        let last_lte = p.finding__last_discovery_at__lte ? dayjs(p.finding__last_discovery_at__lte).format("YYYY/MM/DD") : "";

        filters = {
            asset__kind__in: toArray(p.asset__kind__in),
            finding__status__in: toArray(p.finding__status__in),
            vulnerability__level__in: toArray(p.vulnerability__level__in),
            vulnerability__id__ilike: p.vulnerability__id__ilike || "",
            vulnerability_tag_id: p.vulnerability_tag_id || "",
            finding__first_discovery_at: first_gte && first_lte ? `${first_gte} - ${first_lte}` : "",
            finding__last_discovery_at: last_gte && last_lte ? `${last_gte} - ${last_lte}` : ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return { id: opt.id, name: opt.name || opt.title_es || opt.title || opt.email || opt.id };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    function flatpickrRangeAction(node, params) {
        let currentValue = params.value || "";

        const fp = flatpickr(node, {
            mode: "range",
            dateFormat: "Y/m/d",
            locale: $locale === "es" ? Spanish : English,
            defaultDate: params.value ? params.value.split(" - ") : [],
            onChange: (selectedDates) => {
                // Force strict manual formatting to avoid Spanish " a " or English " to " strings
                if (selectedDates.length === 2) {
                    const formatDate = (d) => {
                        let y = d.getFullYear();
                        let m = String(d.getMonth() + 1).padStart(2, "0");
                        let day = String(d.getDate()).padStart(2, "0");
                        return `${y}/${m}/${day}`;
                    };

                    const internalValue = `${formatDate(selectedDates[0])} - ${formatDate(selectedDates[1])}`;
                    currentValue = internalValue;
                    params.onChange(internalValue);
                } else if (selectedDates.length === 0) {
                    currentValue = "";
                    params.onChange("");
                }
            },
            onClose: (selectedDates, dateStr, instance) => {
                if (selectedDates.length === 1) {
                    instance.clear();
                    currentValue = "";
                    params.onChange("");
                }
            },
        });

        return {
            update(newParams) {
                params = newParams;
                if (fp.isOpen) return;

                if (newParams.value === currentValue) return;
                currentValue = newParams.value || "";

                if (newParams.value) {
                    fp.setDate(newParams.value.split(" - "), false);
                } else {
                    fp.clear(false);
                }
            },
            destroy() {
                fp.destroy();
            },
        };
    }

    const getTags = async () => await tagService.search("v2/tag/", {});

    const handleEmptyFilters = (filtersObj = {}) => {
        const clean = {};
        for (let [key, val] of Object.entries(filtersObj)) {
            if (val !== "" && val !== null && val !== undefined) {
                clean[key] = val;
            }
        }
        return clean;
    };

    const handleVulsFilter = () => {
        let returnValue = {
            property_kind: kind,
            property_name: name,
        };
        if (currentFilter.vulnerability_tag_id) returnValue.tag_id = currentFilter.vulnerability_tag_id;
        if (currentFilter.asset__kind__in) returnValue.asset__kind__in = currentFilter.asset__kind__in;
        if (currentFilter.finding__status__in) returnValue.finding__status__in = currentFilter.finding__status__in;
        if (currentFilter.vulnerability__level__in) returnValue.vulnerability__level__in = currentFilter.vulnerability__level__in;
        if (currentFilter.vulnerability__id__ilike) returnValue.vulnerability__id__ilike = currentFilter.vulnerability__id__ilike;
        if (currentFilter.finding__first_discovery_at__gte) returnValue.finding__first_discovery_at__gte = currentFilter.finding__first_discovery_at__gte;
        if (currentFilter.finding__first_discovery_at__lte) returnValue.finding__first_discovery_at__lte = currentFilter.finding__first_discovery_at__lte;
        if (currentFilter.finding__last_discovery_at__gte) returnValue.finding__last_discovery_at__gte = currentFilter.finding__last_discovery_at__gte;
        if (currentFilter.finding__last_discovery_at__lte) returnValue.finding__last_discovery_at__lte = currentFilter.finding__last_discovery_at__lte;
        return returnValue;
    };

    let vulnerabilityFilters = $derived.by(() => handleVulsFilter());

    function handleFilterChange() {
        let updatedFilter = { ...$params };

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        } else {
            delete updatedFilter.asset__kind__in;
        }

        if (filters.finding__status__in && filters.finding__status__in.length > 0) {
            updatedFilter.finding__status__in = filters.finding__status__in.join(",");
        } else {
            delete updatedFilter.finding__status__in;
        }

        if (filters.vulnerability__level__in && filters.vulnerability__level__in.length > 0) {
            updatedFilter.vulnerability__level__in = filters.vulnerability__level__in.join(",");
        } else {
            delete updatedFilter.vulnerability__level__in;
        }

        if (filters.vulnerability_tag_id) {
            updatedFilter.vulnerability_tag_id = filters.vulnerability_tag_id;
        } else {
            delete updatedFilter.vulnerability_tag_id;
        }

        if (filters.vulnerability__id__ilike) {
            updatedFilter.vulnerability__id__ilike = filters.vulnerability__id__ilike;
        } else {
            delete updatedFilter.vulnerability__id__ilike;
        }

        // Date processing
        if (filters.finding__first_discovery_at && filters.finding__first_discovery_at.includes(" - ")) {
            let dates = filters.finding__first_discovery_at.split(" - ");
            updatedFilter.finding__first_discovery_at__gte = dates[0].replaceAll("/", "-") + "T00:00:00.000-03:00";
            updatedFilter.finding__first_discovery_at__lte = dates[1].replaceAll("/", "-") + "T23:59:59.999-03:00";
            delete updatedFilter.finding__first_discovery_at;
        } else {
            delete updatedFilter.finding__first_discovery_at__gte;
            delete updatedFilter.finding__first_discovery_at__lte;
        }

        if (filters.finding__last_discovery_at && filters.finding__last_discovery_at.includes(" - ")) {
            let dates = filters.finding__last_discovery_at.split(" - ");
            updatedFilter.finding__last_discovery_at__gte = dates[0].replaceAll("/", "-") + "T00:00:00.000-03:00";
            updatedFilter.finding__last_discovery_at__lte = dates[1].replaceAll("/", "-") + "T23:59:59.999-03:00";
            delete updatedFilter.finding__last_discovery_at;
        } else {
            delete updatedFilter.finding__last_discovery_at__gte;
            delete updatedFilter.finding__last_discovery_at__lte;
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);
        redirect(".", { kind: kind, name: name, ...cleanFilters });
    }

    const clearFilters = () => {
        filters = {
            asset__kind__in: [],
            finding__status__in: [],
            vulnerability__level__in: [],
            vulnerability__id__ilike: "",
            vulnerability_tag_id: "",
            finding__first_discovery_at: "",
            finding__last_discovery_at: "",
        };
        redirect(".", { kind: kind, name: name });
    };

    const clearDateFilter = (fieldName) => {
        filters[fieldName] = "";
    };

    const parseRows = (pageContent) => {
        const cleanParams = handleEmptyFilters(currentFilter);
        pageContent.forEach((row) => {
            if (row.name) {
                row.name = {
                    id: row.id,
                    name: row.name,
                    kind: row.kind,
                    filters: { ...cleanParams },
                };
                if (row.asset_id) row.name.target_object_type = "asset";
                if (row.finding_id) row.name.target_object_type = "finding";
                if (row.vulnerability_id) row.name.target_object_type = "vulnerability";
            }
            if (row.hasOwnProperty("value")) {
                const propName = typeof row.name === "object" ? row.name.name : row.name;
                row.value = {
                    kind: kind,
                    name: propName,
                    value: row.value || null,
                    count: row.count,
                    target_object_type: "vulnerability",
                    filters: { ...cleanParams },
                };
            }
        });
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "vulnerabilityPropTable",
        rowsPerPageOptions: [5, 10, 20, 50, 100],
        rowsPerPage: 5,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: powerTableService.getTableTranslations(),
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let ptInstructs = [
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "count",
            title: $format("list.columns.amount"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
    ];

    // FIXED: state_unsafe_mutation fix applied here (no totalCount mutating in render phase)
    const getProps = async (kindParam, nameParam, _filter) => {
        if (!token.access_token) return { data: [], total: 0 };
        
        const cleanFilters = handleEmptyFilters({
            ...currentFilter,
            kind: kindParam,
            name: nameParam,
        });
        let props = await searchProps({ filter: cleanFilters });

        let data = groupPropsByValue(props || []);
        let total = 0;

        for (let item of data) {
            item.count = item.vulnerability_id.length;
            total = total + item.count;
        }
        data.sort((a, b) => b.count - a.count);
        
        return {
            data,
            total
        };
    };

    const getVulnerabilities = async (_filter) => {
        if (!token.access_token) return { vulnerabilities: [], tags: [] };
        
        let vulnerabilities = await vulnerabilitiesApi.fetchVulnerabilities(
            "v2/vulnerability/",
            {
                filter: {
                    property_kind: kind,
                    property_name: name,
                    ...vulnerabilityFilters,
                },
            },
        );
        let tags = await getTags();
        let associations = await getAssociations();
        for (let vulnerability of vulnerabilities) {
            vulnerability.tag = [];
            for (let tag of tags) {
                for (let assoc of associations) {
                    if (assoc.target_object_id === vulnerability.id && assoc.tag_id === tag.id) {
                        vulnerability.tag.push(tag);
                    }
                }
            }
        }
        return {
            vulnerabilities: vulnerabilities,
            tags: tags,
        };
    };

    const getAssociations = async () => {
        if (!token.access_token) return [];
        return await tagService.search("v2/tag/association/", {
            filter: { target_object_type: "Vulnerability" },
        });
    };

    const groupPropsByValue = (props = []) => {
        let groupedProps = props.reduce((a, b) => {
            const found = a.find((e) => e.value == b.value);
            return (
                found
                    ? found.vulnerability_id.push(b.vulnerability_id)
                    : a.push({ ...b, vulnerability_id: [b.vulnerability_id] }),
                a
            );
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.vulnerability_id);
            prop.vulnerability_id = Array.from(count);
        }
        return groupedProps;
    };

    let vulListOptions = {
        uniquePrefix: "vulnerabilityTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: powerTableService.getTableTranslations(),
        isDataRemote: false,
    };

    let vulListInstructs = [
        { key: "select", title: "", filterable: false, sortable: false },
        {
            key: "priority",
            title: $format("list.columns.priority"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "title_es",
            title: $format("list.columns.title"),
            filterable: false,
            parseAs: "component",
            dataComponent: VulnerabilityTitleRow,
        },
        {
            key: "open",
            title: $format("list.columns.open"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "accepted",
            title: $format("list.columns.accepted"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "fixed",
            title: $format("list.columns.fixed"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "false_positive",
            title: $format("list.columns.false_positive"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "cvss",
            title: "CVSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "epss",
            title: "EPSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "tag",
            title: $format("list.columns.tag"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ];
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">{kind == name ? "" : kind + " - "} {name}</h1>
            </div>
            <div class="col-6">
                <BreadCrumbNav
                    routes={[
                        {
                            label: $format("page.properties.vul_props") || "Vulnerability Properties",
                            path: "/property/vulnerability",
                        },
                        {
                            label: kind,
                            path: "/property/vulnerability/[kind]",
                            params: { kind: kind },
                        },
                        { label: name },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="card mt-3 card-secondary card-outline no-export">
        <div class="card-header">
            <h4 class="card-title">
                {$format("components.advancedSearch.search")}
            </h4>
        </div>
        <div class="card-body pe-5">
            <div class="row mb-3">
                <div class="col-12 col-md-4">
                    <label for="vulnerability__level__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                        {$format("vulnerability.severity.title")}
                    </label>
                    <select
                        id="vulnerability__level__in"
                        name="vulnerability__level__in"
                        multiple
                        use:tomSelectAction={{
                            options: priorityOptions,
                            value: filters.vulnerability__level__in,
                            placeholder: $format("buttons.search"),
                            onChange: (val) => (filters.vulnerability__level__in = val),
                        }}
                    ></select>
                </div>
                <div class="col-12 col-md-4">
                    <label for="vulnerability__id__ilike" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                        {$format("components.advancedSearch.vulnerability_kind")}
                    </label>
                    <select
                        class="form-select"
                        name="vulnerability__id__ilike"
                        bind:value={filters.vulnerability__id__ilike}
                    >
                        <option value="">--</option>
                        {#each vulKindOptions as opt}
                            <option value={opt.id}>{opt.name}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-12 col-md-4">
                    {#await getTags()}
                        <label for="vulnerability_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                            {$format("components.advancedSearch.vulnerability_tag") || "Vulnerability Tag"}
                        </label>
                        <select class="form-select" disabled>
                            <option value="">--</option>
                        </select>
                    {:then tags}
                        <label for="vulnerability_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                            {$format("components.advancedSearch.vulnerability_tag") || "Vulnerability Tag"}
                        </label>
                        <select
                            class="form-select"
                            name="vulnerability_tag_id"
                            bind:value={filters.vulnerability_tag_id}
                        >
                            <option value="">--</option>
                            {#each tags as opt}
                                <option value={opt.id}>{opt.name}</option>
                            {/each}
                        </select>
                    {/await}
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 col-md-6">
                    <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                        {$format("components.advancedSearch.asset_kind")}
                    </label>
                    <select
                        id="asset__kind__in"
                        name="asset__kind__in"
                        multiple
                        use:tomSelectAction={{
                            options: translatedAssetKinds,
                            value: filters.asset__kind__in,
                            placeholder: $format("buttons.search"),
                            onChange: (val) => (filters.asset__kind__in = val),
                        }}
                    ></select>
                </div>
                <div class="col-12 col-md-6">
                    <label for="finding__status__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                        {$format("components.advancedSearch.status_of_findings")}
                    </label>
                    <select
                        id="finding__status__in"
                        name="finding__status__in"
                        multiple
                        use:tomSelectAction={{
                            options: statusOptions,
                            value: filters.finding__status__in,
                            placeholder: $format("buttons.search"),
                            onChange: (val) => (filters.finding__status__in = val),
                        }}
                    ></select>
                </div>
            </div>

            <div class="row mb-3">
                {#each dateInputs as field}
                    <div class="col-12 col-md-6">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <label for={field.id} class="form-label text-muted fw-bold mb-0" style="font-size: 0.85rem;">
                                {field.label}
                            </label>
                            <a
                                href="#"
                                class="text-danger text-decoration-none"
                                style="font-size: 0.75rem;"
                                onclick={(e) => {
                                    e.preventDefault();
                                    clearDateFilter(field.name);
                                }}
                            >
                                {$format("clean")}
                            </a>
                        </div>
                        <div class="input-group input-group-sm">
                            <span class="input-group-text bg-light text-muted">
                                <i class="far fa-calendar-alt"></i>
                            </span>
                            <input
                                type="text"
                                class="form-control bg-white cursor-pointer"
                                id={field.id}
                                name={field.name}
                                placeholder="YYYY/MM/DD - YYYY/MM/DD"
                                readonly
                                use:flatpickrRangeAction={{
                                    value: filters[field.name],
                                    onChange: (val) => (filters[field.name] = val),
                                }}
                            />
                        </div>
                    </div>
                {/each}
            </div>

            <div class="btn-strip pt-3 mt-3 border-top pb-3">
                <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                    <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                </button>
                <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                    <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                </button>
            </div>
        </div>
    </div>

    <div class="col-12 d-flex">
        {#await getProps(kind, name, currentFilter)}
            <div class="card mt-3 w-100" style="min-height: 500px; display: flex; justify-content: center; align-items: center;">
                <Spinner />
            </div>
        {:then propsResult}
            {#key ptOptions.rowsPerPage}
                <div class="card mt-3 w-100" style="flex: 1;">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("values")} ({propsResult.total})
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <PowerTable
                                ptData={propsResult.data}
                                {ptInstructs}
                                {ptOptions}
                                bind:this={tableElement}
                            />
                        </div>
                    </div>
                </div>
                <div class="card mt-3 w-100" style="flex: 1;">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.properties.findings_per_value")}
                        </div>
                    </div>
                    <div class="card-body" style="padding: 1.5em 7em;">
                        <CountByPropValue
                            data={propsResult.data}
                            linkTo={"values"}
                            propertyKind={kind}
                            type={"vulnerability"}
                            filter={currentFilter}
                        />
                    </div>
                </div>
            {/key}
        {/await}
    </div>

    <div class="row">
        <div class="col-12">
            {#await getVulnerabilities(currentFilter)}
                <div class="d-flex justify-content-center"><Spinner /></div>
            {:then vulnerability_data}
                <div class="card mt-3">
                    <VulnerabilityList
                        data={vulnerability_data.vulnerabilities}
                        ptInstructs={vulListInstructs}
                        tags={vulnerability_data.tags}
                        showActions={false}
                        ptOptions={vulListOptions}
                    />
                </div>
            {/await}
        </div>
    </div>
</section>

<style>
    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }

    .cursor-pointer {
        cursor: pointer !important;
    }

    .d-flex {
        gap: 15px;
    }
</style>